import {
	Avatar,
	Box,
	IconButton,
	Menu,
	MenuItem,
	Pagination,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';
import i18n from 'i18n';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import {
	deleteUserDocument,
	embedDocument,
	getDocumentUrl,
	getUserDocuments,
} from 'store/user-service/asyncActions';
import { getIsLoading, getUserFiles, getUserFilesPagination } from 'store/user-service/selector';
import { setDocumentProcessing } from 'store/user-service/userSlice';

import { flexMixin } from 'utils/flexMixin';

import { NamespacesList } from 'core/enums/common';
import { errorNotify } from 'core/helpers/toasts';
import { useGetUser } from 'core/hooks/useGetUser';
import { UserFile } from 'core/types/base';

import { LongTextTooltip } from 'components/shared/LongTextTooltip';

import { ReactComponent as DocumentUpload } from 'assets/icons/document-upload.svg';
import { ReactComponent as File } from 'assets/icons/document.svg';
import { ReactComponent as ActionMenu } from 'assets/icons/more-vertical.svg';
import { ReactComponent as Pdf } from 'assets/icons/pdf.svg';
import { ReactComponent as AllowAccess } from 'assets/icons/profile-add.svg';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';

import { AllowAccessModal } from './AllowAccessModal';
import { DeleteFileModal } from './DeleteFileModal';
import { FailedFileProcessingModal } from './FailedFileProcessingModal';
import { DocumentHeader } from './Header';
import { TableHeader, fileOperationsErrors } from './helpers';
import * as Styled from './styled';

export const Documents = () => {
	const { t: profileTranslate } = useTranslation(NamespacesList['profilePage']);
	const { t: commonTranslate } = useTranslation(NamespacesList['common']);

	const dispatch = useAppDispatch();

	const [deletingLoading, setDeletingLoading] = useState(false);
	const [currentFile, setCurrentFile] = useState<UserFile | null>(null);
	const [isOpenErrorModal, setIsOpenErrorModal] = useState(false);
	const [isOpenAccessModal, setIsOpenAccessModal] = useState(false);
	const [uploadingFileError, setUploadingFileError] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const { user } = useGetUser();
	const open = Boolean(anchorEl);

	const isLoading = useSelector(getIsLoading);
	const userFiles = useSelector(getUserFiles);
	const userFilesPagination = useSelector(getUserFilesPagination);

	const handleClickOnMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleActionErrorModal = () => {
		setIsOpenErrorModal((prev) => !prev);
	};

	const handleActionAccessModal = () => {
		setIsOpenAccessModal((prev) => !prev);
	};

	const handleActionFileProcessingErrorModal = () => {
		setUploadingFileError((prev) => !prev);
	};

	const handleOpenFile = async () => {
		try {
			const defaultObject = { fileName: currentFile?.name || '' };
			const url = await dispatch(
				getDocumentUrl(
					user.id !== currentFile?.userId
						? { ...defaultObject, ownerId: currentFile?.userId }
						: defaultObject
				)
			).unwrap();
			window.open(url, '_blank', 'noreferrer');
		} catch (error) {
			const { title, description } = fileOperationsErrors(commonTranslate).failedOperation;
			errorNotify(title, description);
		} finally {
			setCurrentFile(null);
		}
	};

	const menuItems = useMemo(
		() => [
			{
				onClick: handleOpenFile,
				title: 'menu.open',
				icon: <File style={{ width: '20px', height: '20px' }} />,
			},
			{
				onClick: handleOpenFile,
				title: 'menu.download',
				icon: <DocumentUpload style={{ width: '20px', height: '20px' }} />,
			},
			{
				onClick: handleActionAccessModal,
				title: 'menu.allowAccess',
				icon: <AllowAccess style={{ width: '20px', height: '20px' }} />,
			},
			{
				onClick: handleActionErrorModal,
				title: 'menu.delete',
				icon: <Trash style={{ width: '20px', height: '20px' }} />,
			},
		],
		[currentFile]
	);

	const tableHeaders: TableHeader[] = useMemo(
		() => [
			{
				label: 'text.file',
				align: i18n?.language === 'ar' ? 'right' : 'left',
			},
			{
				label: 'text.status',
				align: i18n?.language === 'ar' ? 'left' : 'right',
			},
			{
				label: 'text.date',
				align: i18n?.language === 'ar' ? 'left' : 'right',
			},
			{
				label: 'text.owner',
				align: i18n?.language === 'ar' ? 'right' : 'left',
			},
			{
				label: null,
				align: i18n?.language === 'ar' ? 'left' : 'right',
			},
		],
		[i18n?.language]
	);

	const getDayMonthYear = (inputDate: string) => {
		const dateObject = new Date(inputDate);

		if (!isNaN(dateObject.getTime())) {
			const dayNumber = dateObject.getUTCDate();
			const month = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(dateObject);
			const year = dateObject.getUTCFullYear();

			return `${dayNumber} ${month}, ${year}`;
		} else {
			return null;
		}
	};

	const handleDeleteFile = async (fileName: string) => {
		setDeletingLoading(true);
		try {
			await dispatch(deleteUserDocument({ fileName })).unwrap();
			await dispatch(getUserDocuments({ currentPageNumber: 1, recordsPerPage: 5 }));
		} catch (error) {
			const { title, description } = fileOperationsErrors(commonTranslate).failedOperation;
			errorNotify(title, description);
		} finally {
			handleActionErrorModal();
			setDeletingLoading(false);
		}
	};

	const handleEmbedDocument = async (selectedFile: UserFile) => {
		const fileName = selectedFile.name.replace('.pdf', '');

		const dispatchSetDocumentProcessing = async (status: boolean | null) => {
			await dispatch(setDocumentProcessing({ name: fileName, status }));
		};

		try {
			await dispatchSetDocumentProcessing(null);
			await dispatch(embedDocument({ fileName })).unwrap();
		} catch (error) {
			await dispatchSetDocumentProcessing(false);
			handleActionFileProcessingErrorModal();
		}
	};

	const getFileStatus = (file: UserFile) => {
		switch (file.isReadyToUse) {
			case null:
				return (
					<Styled.StatusBox type="processing" className="rtl-able">
						{profileTranslate('text.processing')}
					</Styled.StatusBox>
				);
			case true:
				return (
					<Styled.StatusBox type="ready" className="rtl-able">
						{profileTranslate('text.ready')}
					</Styled.StatusBox>
				);
			case false:
				return (
					<Styled.StatusBox
						type="notReady"
						className="rtl-able"
						onClick={() => handleEmbedDocument(file)}
					>
						{profileTranslate('text.notReady')}
					</Styled.StatusBox>
				);
			default:
				return null;
		}
	};

	useEffect(() => {
		dispatch(getUserDocuments({ currentPageNumber: 1, recordsPerPage: 5 }));
	}, []);

	const checkingDocument = useMemo(
		() => !isLoading && userFiles && userFiles.length > 0,
		[isLoading, userFiles]
	);

	const getPageNumber = useMemo(() => {
		if (userFilesPagination) {
			return Math.ceil(
				userFilesPagination?.totalRecordsFound / userFilesPagination?.recordsPerPage
			);
		}

		return 1;
	}, [userFilesPagination]);

	const handleChangePage = async (_event: ChangeEvent<unknown>, value: number) => {
		if (value !== userFilesPagination?.currentPageNumber) {
			await dispatch(getUserDocuments({ currentPageNumber: value, recordsPerPage: 5 }));
		}
	};

	return (
		<Styled.Documents className="rtl-able">
			<DocumentHeader
				handleActionFileProcessingErrorModal={handleActionFileProcessingErrorModal}
				handleEmbedDocument={handleEmbedDocument}
			/>

			<Table sx={{ minWidth: 650 }}>
				<TableHead>
					<TableRow>
						{tableHeaders.map((header) => (
							<TableCell key={header.label} align={header.align}>
								{header.label ? profileTranslate(header.label) : null}
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				{checkingDocument && (
					<TableBody>
						{userFiles?.map((file) => (
							<TableRow key={file.name}>
								<TableCell>
									<Styled.FileName>
										<Pdf />
										<LongTextTooltip title={file.name || 'File name'} sx={{ maxWidth: '200px' }} />
									</Styled.FileName>
								</TableCell>
								<TableCell align={i18n?.language === 'ar' ? 'left' : 'right'}>
									<Styled.StatusField>{getFileStatus(file)}</Styled.StatusField>
								</TableCell>
								<TableCell align={i18n?.language === 'ar' ? 'left' : 'right'}>
									<Styled.FileDate>{getDayMonthYear(file.creationDate)}</Styled.FileDate>
								</TableCell>
								<TableCell>
									<Styled.Owner>
										<Avatar sx={{ bgcolor: '#1976d2' }} src={file.avatarLink}>
											{!file.avatarLink && `${file.firstName[0]}${file.lastName[0]}`}
										</Avatar>
										<Styled.OwnerInfo>
											<Typography variant="h4">{`${file.firstName} ${file.lastName}`}</Typography>
											<Typography>{file.email}</Typography>
										</Styled.OwnerInfo>
									</Styled.Owner>
								</TableCell>
								<TableCell align={i18n?.language === 'ar' ? 'left' : 'right'}>
									<IconButton
										onClick={(e: any) => {
											handleClickOnMenu(e);
											setCurrentFile(file);
										}}
										sx={{
											'&.Mui-disabled': {
												opacity: 0.4,
											},
										}}
									>
										<ActionMenu />
									</IconButton>
									<Menu
										className="rtl-able"
										PaperProps={{
											style: {
												width: 212,
												marginTop: '12px',
												boxShadow: '0px 12px 24px 0px rgba(40, 103, 131, 0.12)',
											},
										}}
										anchorEl={anchorEl}
										anchorOrigin={{
											vertical: 'bottom',
											horizontal: i18n?.language === 'ar' ? 'left' : 'right',
										}}
										transformOrigin={{
											vertical: 'top',
											horizontal: i18n?.language === 'ar' ? 'left' : 'right',
										}}
										open={open}
										onClose={handleClose}
										onClick={handleClose}
									>
										{menuItems.map((item, index) => {
											return (
												<MenuItem
													onClick={item.onClick}
													key={item.title}
													disabled={currentFile?.userId !== user.id && index !== 0}
												>
													<Box
														sx={{
															...flexMixin({ gap: 12 }),
														}}
													>
														{item.icon}
														<Typography>{commonTranslate(item.title)}</Typography>
													</Box>
												</MenuItem>
											);
										})}
									</Menu>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				)}
			</Table>

			{isLoading ? (
				<Styled.LoaderBox>
					<Oval
						height={34}
						width={34}
						color="#fff"
						wrapperStyle={{}}
						wrapperClass=""
						visible={true}
						ariaLabel="oval-loading"
						secondaryColor="#91B6FF"
						strokeWidth={5}
						strokeWidthSecondary={5}
					/>
				</Styled.LoaderBox>
			) : (
				userFiles?.length === 0 && (
					<Styled.EmptyState>
						<File />
						<Typography variant="h3">{profileTranslate('text.emptyFileState')}</Typography>
						<Typography>{profileTranslate('text.downloadFirstFile')}</Typography>
					</Styled.EmptyState>
				)
			)}

			{checkingDocument && (
				<Pagination
					count={getPageNumber}
					page={userFilesPagination?.currentPageNumber}
					onChange={handleChangePage}
					variant="outlined"
					shape="rounded"
					sx={{ marginTop: 'auto' }}
				/>
			)}

			<DeleteFileModal
				isOpen={isOpenErrorModal}
				onClose={handleActionErrorModal}
				onConfirm={handleDeleteFile}
				isLoading={deletingLoading}
				currentFile={currentFile}
			/>
			<AllowAccessModal
				isOpen={isOpenAccessModal}
				onClose={handleActionAccessModal}
				currentFile={currentFile}
			/>
			<FailedFileProcessingModal
				isOpen={uploadingFileError}
				onClose={handleActionFileProcessingErrorModal}
			/>
		</Styled.Documents>
	);
};
